import {
  type FC,
  Fragment,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";

import { useQuery } from "@apollo/client/react";

import { DashboardProjectsDetailsDocument } from "@app_schema";

import { useIsDesktop } from "@application/hooks/use_is_desktop";
import { usePolling } from "@application/hooks/use_polling";

import { track } from "@application/utilities/track";

import { EditLink } from "@styled/edit_link";
import { Page } from "@styled/page";
import { PillGroup } from "@styled/pill_group";

import { type IntakeType } from "./case_status_lines";
import { EvalTypeGate } from "./eval_type_gate";
import { formatDob } from "./format_dob";
import { GuidedEmptyState } from "./guided_empty_state";
import { ProjectsDocuments } from "./project_documents";
import { ProjectMenu } from "./project_menu";
import { ProjectNotificationBanner } from "./project_notification_banner";
import { useLandingSection } from "./use_landing_section";

export const DashboardProjectsDetails: FC = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { id } = useParams<{ id: string }>();
  if (!id) throw new Error("undefined ID");

  const { data, startPolling, stopPolling } = useQuery(
    DashboardProjectsDetailsDocument,
    {
      variables: { id },
      // The route loader has already fetched this over the network; asking
      // again on mount only doubled the largest query on the page. Polling
      // below still refreshes it while the case is processing.
      fetchPolicy: "cache-first",
    },
  );

  // The single selected section drives BOTH the work area and the assistant
  // scope — one source of truth, nothing to sync. Null until the user picks, so
  // it falls back to a sensible default on first load.
  const [section, setSection] = useState<IntakeType | null>(null);
  const landingSection = useLandingSection(id, data?.project.landingSection);
  // Spoken on the bubbles -> tab-bar handoff. It lives here, not in
  // GuidedEmptyState: that component unmounts the moment documentsCount flips,
  // so a live region inside it would be gone before it could announce.
  const [announcement, setAnnouncement] = useState("");
  // `← Change` reopens the eval-type picker. It lives here because the picker
  // is a page-level gate, and it is LOCAL state: opening the picker must not
  // touch the server, or merely looking at your options clears the case's type.
  const [changing, setChanging] = useState(false);

  usePolling({
    startPolling,
    stopPolling,
    poll:
      (data?.project?.readingFiles ?? false) ||
      (data?.project?.recordReview?.polling ?? false),
    // The server decides WHEN to poll; this only sets how often. Every tick
    // refetches the full project query and holds a Puma thread while it runs.
    interval: 10000,
  });

  useEffect(() => {
    track("project_viewed", { project_id: id });
  }, [id]);

  // Announce on the 0 -> non-zero transition rather than from an upload
  // callback: records don't attach through the shared save path at all (they
  // run the review workflow inside ProcessRecordsCard), so a callback would
  // miss the default and most common first upload. The count is the one signal
  // every bucket moves.
  // Keyed by project id, not just a boolean: the route renders this component
  // without a `key`, so React reuses the instance across :id changes. Without
  // the id an empty case followed by a populated one would step 0 -> N and
  // announce an upload the user never made.
  const documentsCount = data?.project?.documentsCount;
  const seen = useRef({ id, empty: false });

  useEffect(() => {
    if (documentsCount === undefined) return;

    const previous = seen.current;
    seen.current = { id, empty: documentsCount === 0 };

    // Clearing on a project change matters: setting the SAME string is a no-op
    // in React, the text node never changes, and a live region that never
    // changes never speaks. Without this the second case of a session is silent.
    if (previous.id !== id) {
      setAnnouncement("");
      return;
    }

    if (previous.empty && documentsCount > 0) {
      setAnnouncement("Upload complete. Opening your case.");
    }
  }, [id, documentsCount]);

  if (!data) return null;
  const { project } = data;

  const metaItems: ReactNode[] = [];
  if (project.claimantDob) {
    metaItems.push(
      <span key="dob">DOB: {formatDob(project.claimantDob)}</span>,
    );
  }
  if (!project.user.me) {
    metaItems.push(
      <span key="assignee">
        Assigned to <strong>{project.user.name}</strong>
      </span>,
    );
  }
  if (project.description) {
    metaItems.push(<span key="desc">{project.description}</span>);
  }

  const headerMeta =
    metaItems.length > 0 ? (
      <>
        {metaItems.map((item, i) => (
          <Fragment key={i}>
            {i > 0 && <span>•</span>}
            {item}
          </Fragment>
        ))}
      </>
    ) : undefined;

  // On a phone Edit moves into ⋯, leaving room for the Assistant beside it.
  const headerRight = (
    <PillGroup>
      {project.policy.modify && isDesktop && (
        <EditLink to={`/dashboard/projects/${project.id}/edit`} color="white" />
      )}
      <ProjectMenu
        project={project}
        showEdit={project.policy.modify && !isDesktop}
        onDestroy={() => navigate("/dashboard/projects")}
      />
    </PillGroup>
  );

  // Setup gate: until an evaluation type is chosen the page shows only the
  // picker — the type decides how records are organized, so it precedes the
  // work UI (assistant + intake), rather than living inside the Records pane.
  // The header (Edit / ⋯) stays so a freshly created project — which lands here
  // with no type yet — can still be edited or deleted.
  // Two ways in: no type yet (first run), or `← Change` on a committed one.
  // `current` is what makes the picker open on the case's ACTUAL type; it is
  // null on first run, the only case that defaults to Comprehensive. `onClose`
  // is passed only when there is a committed type to go back to.
  if (!project.evaluationType || changing) {
    return (
      <Page
        heading={project.name}
        headerMeta={headerMeta}
        headerRight={headerRight}
      >
        <EvalTypeGate
          projectId={project.id}
          current={project.evaluationType}
          onClose={
            project.evaluationType ? () => setChanging(false) : undefined
          }
        />
      </Page>
    );
  }

  // Second setup gate: a case with no documents gets the guided empty state
  // instead of a tab bar over four empty panes. It sits AFTER the eval-type
  // gate because the type decides how records are organized. Once any document
  // lands `documentsCount` flips and the normal view takes over.
  // Rendered at the same child index in both branches so React keeps the same
  // DOM node across the swap — a remounted live region never announces.
  const liveRegion = (
    <div aria-live="polite" className="sr-only">
      {announcement}
    </div>
  );

  if (project.documentsCount === 0) {
    return (
      <Page
        heading={project.name}
        headerMeta={headerMeta}
        headerRight={headerRight}
      >
        {liveRegion}
        <ProjectNotificationBanner />

        <GuidedEmptyState
          project={project}
          onSelectSection={setSection}
          onChangeEvalType={() => setChanging(true)}
        />
      </Page>
    );
  }

  // Until the user picks, the case opens on its first section holding a file
  // (the server decides): Records, then Final Report, Dictation, Supporting.
  const selectedSection = section ?? landingSection;

  return (
    <Page
      heading={project.name}
      headerMeta={headerMeta}
      headerRight={headerRight}
    >
      {liveRegion}
      <ProjectNotificationBanner />

      <ProjectsDocuments
        key={project.id}
        project={project}
        selectedType={selectedSection}
        onSelectType={(next) => {
          setSection(next);
          track("section_selected", { project_id: id, section: next });
        }}
        onChangeEvalType={() => setChanging(true)}
      />
    </Page>
  );
};
